import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Svg from '../../../../talhasvg'

//STYLES
const Item = styled.button`
  width: 100%;
  background: #fff;
  box-sizing: border-box;
  padding: 15px;
  display: flex;
  gap: 10px;
  align-items: center;
  cursor: pointer;
  border: none;
  text-align: left;
  font-size: 14px;
  /* outline: none; */

  :hover {
    background-color: var(--hover-color);
  }
  :focus {
    background-color: var(--hover-color);
  }
  :is([data-focused='true']) {
    background-color: var(--hover-color);
  }
  :is([data-current='true']) {
    background-color: #d8e6dc;
    font-weight: 600;
    border-radius: 7px;
  }
  &:is([data-disabled='true']) {
    background-color: var(--disabled-color);
    color: var(--disabled-text-color);
    cursor: not-allowed;
  }
  /* o hover/focus não deve "acender" um item desabilitado */
  &:is([data-disabled='true']):hover,
  &:is([data-disabled='true']):focus {
    background-color: var(--disabled-color);
  }

  div.Item__img_placeholder {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

//PROPS
interface Props {
  children?: React.ReactNode
  index?: number
  focused: boolean
  onClick: Function
  current?: string
  disabled?: boolean
  iconSVGSize?: number
  iconSVG?: [string | number, string | number]
  node?: React.ReactNode
  capitalize?: boolean
  /** Máximo de pontos do polígono do talhão (ver talhasvg) */
  maxPontosSvg?: number
  /** Container com scroll da lista, usado como `root` do IntersectionObserver */
  scrollRoot?: HTMLElement | null
  /** Só observa/renderiza o SVG quando true (ex.: após a animação de abertura) */
  ativoSvg?: boolean
}

export const ItemList: React.FC<Props> = ({
  children,
  focused,
  index,
  onClick,
  current,
  disabled,
  iconSVG,
  iconSVGSize,
  node,
  capitalize,
  maxPontosSvg,
  scrollRoot,
  ativoSvg = true,
}) => {
  // Renderiza o SVG só quando o item entra na viewport, evitando gerar todos os
  // polígonos de uma vez (o que travava a UI em listas com vários talhões).
  const svgWrapperRef = useRef<HTMLDivElement>(null)
  const [svgVisivel, setSvgVisivel] = useState<boolean>(false)

  useEffect(() => {
    if (focused) {
      let elemento = document.getElementById(`item_${index}`)
      elemento.focus()
    }
  }, [focused])

  useEffect(() => {
    const el = svgWrapperRef.current
    if (!el || svgVisivel || !iconSVG || !ativoSvg) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setSvgVisivel(true)
          observer.disconnect()
        }
      },
      // `root` precisa ser o container com scroll da lista: observando contra a
      // viewport (root: null) a lista inteira já está visível e TODOS os SVGs
      // seriam gerados de uma vez, anulando o lazy-load.
      { root: scrollRoot ?? null, rootMargin: '100px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [iconSVG, svgVisivel, scrollRoot, ativoSvg])

  return (
    <Item
      data-focused={focused}
      disabled={disabled}
      data-disabled={disabled}
      data-current={current === children}
      id={`item_${index}`}
      onClick={() => onClick()}
      style={{ textTransform: capitalize ? 'capitalize' : 'none' }}
    >
      {!!iconSVG && (
        <div ref={svgWrapperRef} className='Item__img_placeholder' style={{ width: iconSVGSize, height: iconSVGSize }}>
          {svgVisivel && <Svg fill='green' coordinates={iconSVG} maxPontos={maxPontosSvg} />}
        </div>
      )}

      {capitalize ? String(children).toLowerCase() : children}

      {node && (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          {/*@ts-ignore */}
          <span dangerouslySetInnerHTML={{ __html: node }} />
        </div>
      )}
    </Item>
  )
}
